import React, { useEffect, useRef, useState } from 'react';
import { getAccessToken } from '../../../authUtils';
import { COLORS, Icons, Modal, Btn } from '../../ui/crm-ui';

interface Props {
  baseApiUrl: string;
  open: boolean;
  folderId: string | null;
  folderName?: string;
  onClose: () => void;
  onUploaded?: () => void;
}

type Status = 'pending' | 'uploading' | 'done' | 'error';

interface FileEntry {
  id: number;
  file: File;
  name: string;
  size: number;
  status: Status;
  progress: number;
  errorMessage: string | null;
  xhr: XMLHttpRequest | null;
}

const BLOCKED_EXTENSIONS = ['.exe', '.bat', '.sh', '.cmd', '.ps1', '.vbs'];
const MAX_FILE_SIZE = 100 * 1024 * 1024;

function getExt(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

let nextId = 1;

const BulkUploadModal: React.FC<Props> = ({ baseApiUrl, open, folderId, folderName, onClose, onUploaded }) => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileEntry[]>([]);
  filesRef.current = files;

  useEffect(() => {
    if (!open) {
      // Abort any in-flight uploads when modal closes
      filesRef.current.forEach((f) => { if (f.xhr && f.status === 'uploading') f.xhr.abort(); });
      setFiles([]);
      setDragging(false);
    }
  }, [open]);

  function startUpload(entry: FileEntry) {
    if (!folderId) return;
    const formData = new FormData();
    formData.append('file', entry.file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseApiUrl}/api/documents/upload/${folderId}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, progress, status: 'uploading' } : f)));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, progress: 100, status: 'done', xhr: null } : f)));
        if (onUploaded) onUploaded();
      } else {
        let message = 'Upload failed';
        if (xhr.status === 404) {
          message = 'Folder could not be found in SharePoint.';
        } else {
          try { message = (JSON.parse(xhr.responseText) as { error?: string }).error ?? message; } catch { /* ignore */ }
        }
        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: 'error', errorMessage: message, xhr: null } : f)));
      }
    };

    xhr.onerror = () => {
      setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: 'error', errorMessage: 'Network error', xhr: null } : f)));
    };

    xhr.onabort = () => {
      // No state update needed — modal is closing
    };

    setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: 'uploading', xhr } : f)));

    getAccessToken().then((token) => {
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    }).catch(() => {
      setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: 'error', errorMessage: 'Not authenticated', xhr: null } : f)));
    });
  }

  function addFiles(fileList: File[]) {
    if (!folderId) return;
    const newEntries: FileEntry[] = fileList.map((file) => {
      const ext = getExt(file.name);
      if (BLOCKED_EXTENSIONS.includes(ext)) {
        return { id: nextId++, file, name: file.name, size: file.size, status: 'error', progress: 0, errorMessage: `${ext} files are not allowed`, xhr: null };
      }
      if (file.size > MAX_FILE_SIZE) {
        return { id: nextId++, file, name: file.name, size: file.size, status: 'error', progress: 0, errorMessage: 'File exceeds 100 MB limit', xhr: null };
      }
      return { id: nextId++, file, name: file.name, size: file.size, status: 'pending', progress: 0, errorMessage: null, xhr: null };
    });
    setFiles((prev) => [...prev, ...newEntries]);
    newEntries.forEach((entry) => {
      if (entry.status === 'pending') startUpload(entry);
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (!folderId) return;
    if (e.dataTransfer.files.length) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) {
      addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  }

  function removeEntry(id: number) {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.xhr && target.status === 'uploading') target.xhr.abort();
      return prev.filter((f) => f.id !== id);
    });
  }

  function clearDone() {
    setFiles((prev) => prev.filter((f) => f.status !== 'done'));
  }

  const anyUploading = files.some((f) => f.status === 'uploading' || f.status === 'pending');
  const doneCount = files.filter((f) => f.status === 'done').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={folderName ? `Upload Documents — ${folderName}` : 'Upload Documents'}
      width={620}
      footer={
        <>
          {doneCount > 0 && !anyUploading && (
            <span style={{ marginRight: 'auto', fontSize: 12, color: COLORS.mid }}>
              {doneCount} uploaded{errorCount > 0 ? `, ${errorCount} failed` : ''}
            </span>
          )}
          {files.some((f) => f.status === 'done') && (
            <Btn small onClick={clearDone} disabled={anyUploading}>Clear completed</Btn>
          )}
          <Btn primary onClick={onClose}>{anyUploading ? 'Close (uploads will stop)' : 'Done'}</Btn>
        </>
      }
    >
      <div
        onDragOver={(e) => { e.preventDefault(); if (folderId) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => folderId && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? COLORS.blue : COLORS.border}`,
          background: dragging ? COLORS.blueSoft : folderId ? COLORS.white : COLORS.light,
          borderRadius: 12,
          padding: '32px 20px',
          textAlign: 'center',
          cursor: folderId ? 'pointer' : 'not-allowed',
          transition: 'all 0.15s',
          opacity: folderId ? 1 : 0.6,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={dragging ? COLORS.blue : COLORS.mid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark, marginBottom: 4 }}>
          {dragging ? 'Drop files to upload' : 'Drag & drop files here'}
        </div>
        <div style={{ fontSize: 13, color: COLORS.mid }}>
          or <span style={{ color: COLORS.blue, fontWeight: 600 }}>click to browse</span> (Ctrl/Shift-click to select multiple)
        </div>
        <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 10 }}>
          Max 100 MB per file. Executables not allowed.
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
      </div>

      {!folderId && (
        <div style={{ marginTop: 12, fontSize: 12, color: COLORS.red }}>
          No SharePoint folder available for upload.
        </div>
      )}

      {files.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.mid }}>
              {files.length} file{files.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
            {files.map((f) => (
              <FileRow key={f.id} entry={f} onRemove={() => removeEntry(f.id)} />
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

const FileRow: React.FC<{ entry: FileEntry; onRemove: () => void }> = ({ entry, onRemove }) => {
  const statusColor =
    entry.status === 'done' ? COLORS.green :
    entry.status === 'error' ? COLORS.red :
    entry.status === 'uploading' ? COLORS.blue : COLORS.mid;
  const statusLabel =
    entry.status === 'uploading' ? `${entry.progress}%` :
    entry.status === 'done' ? 'Uploaded' :
    entry.status === 'error' ? 'Failed' : 'Pending';

  return (
    <div style={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 8,
      padding: '10px 12px',
      background: COLORS.white,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 500, color: COLORS.dark,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={entry.name}>
            {entry.name}
          </div>
          <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 2 }}>
            {formatSize(entry.size)}
          </div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: statusColor, minWidth: 60, textAlign: 'right' }}>
          {statusLabel}
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 0 }}
        >
          {Icons.close(COLORS.mid)}
        </button>
      </div>
      {(entry.status === 'uploading' || entry.status === 'done') && (
        <div style={{ marginTop: 8, height: 4, background: COLORS.light, borderRadius: 4, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${entry.progress}%`,
              background: entry.status === 'done' ? COLORS.green : COLORS.blue,
              transition: 'width 0.15s',
            }}
          />
        </div>
      )}
      {entry.status === 'error' && entry.errorMessage && (
        <div style={{ marginTop: 6, fontSize: 11, color: COLORS.red }}>{entry.errorMessage}</div>
      )}
    </div>
  );
};

export default BulkUploadModal;
